import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/catalog-guard`;
const show = (v) => (v === '' || v == null ? '—' : String(v));

/* Catalog Change Guard: syncs that try to move fabrics or change type/openness are held here for explicit confirmation */
export const CatalogGuardBanner = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/holds`, { headers }).then(({ data }) => setD(data)).catch(() => {}), [headers]);
  useEffect(() => { if (token) load(); }, [token, load]);
  if (!d || !d.total) return null;

  const decide = async (h, ok) => {
    let body = {};
    if (ok) {
      const msg = `Apply this change to ${h.color_name} (${h.code})?\n\n${Object.entries(h.changes).map(([k, v]) => `${k}: ${show(v.from)} → ${show(v.to)}`).join('\n')}`;
      if (h.roman_exit) {
        const t = window.prompt(`${msg}\n\nThis moves a ROMAN fabric out of a Roman collection. Type MOVE ROMAN to confirm:`);
        if (t === null) return;
        body = { confirm_text: t };
      } else if (!window.confirm(msg)) return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/holds/${h.id}/${ok ? 'confirm' : 'reject'}`, body, { headers });
      toast.success(ok ? 'Change applied' : 'Change rejected — record left as is');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy(false);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button className="mx-4 sm:mx-6 mt-3 flex w-[calc(100%-2rem)] sm:w-[calc(100%-3rem)] items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-xs text-amber-900" data-testid="catalog-guard-banner">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span><b>{d.total} catalog change{d.total > 1 ? 's' : ''} held for confirmation</b>{d.roman_exits ? ` — ${d.roman_exits} would move Roman fabrics out of a Roman collection` : ''}. A sync tried to change collection, type or openness. Nothing was applied.</span>
          <span className="ml-auto underline">Review</span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Held catalog changes</DialogTitle></DialogHeader>
        <div className="divide-y divide-neutral-100" data-testid="catalog-guard-list">
          {d.items.map(h => (
            <div key={h.id} className="py-2 text-xs flex items-start gap-3" data-testid={`catalog-hold-${h.id}`}>
              <div className="flex-1 space-y-0.5">
                <p className="font-medium text-neutral-900">{h.color_name} · {h.code} · {h.material_name}
                  {h.roman_exit && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-800" data-testid={`catalog-hold-roman-${h.id}`}>ROMAN EXIT</span>}
                </p>
                {Object.entries(h.changes).map(([k, v]) => <p key={k} className="text-neutral-600"><span className="text-neutral-400">{k}:</span> {show(v.from)} → <b>{show(v.to)}</b></p>)}
                <p className="text-neutral-400">From {h.source} · {(h.created_at || '').slice(0, 16).replace('T', ' ')}</p>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busy} onClick={() => decide(h, false)} data-testid={`catalog-hold-reject-${h.id}`}>Reject</Button>
              <Button size="sm" className="h-7 text-[11px]" disabled={busy} onClick={() => decide(h, true)} data-testid={`catalog-hold-confirm-${h.id}`}>Confirm</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
